import { StyleSheet } from 'react-native';

import type { useTheme } from '../theme';

// Dropset chain + set-type menu + per-set comment editor (Track R — split from loggerStyles.ts; pure data,
// merged by the loggerStyles barrel). Not imported directly.
export function dropsetStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    // Dropset chain (1.80.0) — rendered under the parent set inside
    // setRowWrap (so it sits alongside the steppers and details
    // expansion). The Add-dropset entry point lives inside the
    // expanded details panel (toggle "⋯") so the row stays compact for
    // the common case; once attached, the block is always visible
    // because it carries data the user needs to see.
    dropsetAddButton: {
      alignSelf: 'flex-start',
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.ink2,
      backgroundColor: p.surface,
    },
    dropsetAddButtonLocked: {
      opacity: 0.5,
    },
    dropsetAddButtonPressed: {
      opacity: 0.7,
    },
    dropsetAddButtonText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '700',
    },
    dropsetBlock: {
      marginTop: s.s2,
      padding: s.s3,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface2,
      gap: s.s2,
    },
    dropsetHeader: {
      gap: s.s2,
    },
    dropsetTitle: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.3,
    },
    dropsetVariantRow: {
      flexDirection: 'row',
      gap: s.s2,
    },
    dropsetVariantChip: {
      flex: 1,
      minHeight: 36,
      paddingHorizontal: s.s2,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dropsetVariantChipActive: {
      backgroundColor: p.brand500,
      borderColor: p.brand500,
    },
    dropsetVariantChipPressed: {
      opacity: 0.7,
    },
    dropsetVariantChipText: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
    },
    dropsetVariantChipTextActive: {
      color: p.inkOnBrand,
    },
    dropsetHint: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      lineHeight: t.sizes.sm + 4,
    },
    dropsetStepRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    dropsetStepIndex: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: '700',
      width: 24,
    },
    // Logger redesign slice 4 — per-drop completion pill. Smaller than
    // the parent set's pill (28dp vs 36dp) so the chain reads as
    // hierarchically subordinate, but big enough to tap reliably.
    dropsetStepPill: {
      width: 28,
      height: 28,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dropsetStepPillDone: {
      borderColor: p.brand500,
      backgroundColor: p.brand500,
    },
    dropsetStepPillText: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
    },
    dropsetStepPillTextDone: { color: p.inkOnBrand },
    // Slice 4.2 — kg/lb chip inside the stepper bar (focus-on-weight).
    // Lives next to the ⚖ plate icon; same height as the magnitude
    // buttons so the row stays uniform.
    stepperButtonUnit: {
      borderColor: p.borderStrong,
      backgroundColor: p.surface,
    },
    stepperButtonTextUnit: {
      color: p.ink2,
      fontWeight: '700',
      letterSpacing: 0.5,
    },
    // Slice 4.1 — set-type chips shown in the expanded ⋯ panel when
    // a working set has no dropset chain. Replaces the prior
    // "+ Dropset" CTA. Active chip uses brand fill; the others are
    // tap targets to convert.
    setTypeRow: {
      flexDirection: 'row',
      gap: s.s2,
      marginTop: s.s2,
    },
    // The Dropset / Myo-rep set-type chips now come from the shared <Chip dense>
    // primitive (setTypeChip*/Inline styles retired in the consolidation track).
    // Slice 4.3 — ⋯ menu entries (one row per action). Today there's
    // only "Set type" but the layout is ready for "Move ↑↓", "Delete",
    // etc. when slice 5+ adds them.
    setMenuEntry: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s3,
      paddingVertical: s.s3,
      borderRadius: 8,
      backgroundColor: p.surface2,
    },
    setMenuEntryLabel: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    setMenuEntryChevron: {
      color: p.ink3,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    // Slice 4.3 — inline control bar surfaced under the set row when
    // the user activates "Set type" or when the set is already a
    // dropset/myorep. Same row layout the prior DropsetBlock used.
    setTypeBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      marginTop: s.s2,
    },
    // Slice 4.4 — per-set comment editor + display rendered below the
    // set row when the user picks "Set comment" from ⋯ or when the
    // set already carries a note from prior edits.
    setCommentEditor: {
      marginTop: s.s2,
      gap: s.s2,
    },
    setCommentActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: s.s2,
    },
    setCommentDisplay: {
      marginTop: s.s2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderRadius: 6,
      backgroundColor: p.surface2,
    },
    setCommentDisplayText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm + 4,
    },
    // Coach's per-set prescription cue (read-only, from the plan/program).
    // Brand-tinted left rail to distinguish it from the athlete's own grey
    // comment display above.
    setPrescriptionNote: {
      marginTop: s.s2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderRadius: 6,
      backgroundColor: p.surface2,
      borderLeftWidth: 3,
      borderLeftColor: p.ink3,
    },
    setPrescriptionNoteText: {
      color: p.ink,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm + 4,
    },
    // Slice 4.1 — compact dropset control row (variant chips + ? +
    // × remove). Replaces the prior dropsetHeader + dropsetHint pair.
    dropsetControlRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      marginTop: s.s2,
    },
    dropsetControlSpacer: { flex: 1 },
    // Slice 5 — live subtotal shown in place of the parent's reps/weight
    // when the row is a dropset/myorep. The parent isn't a data input in
    // that mode; it's a derived summary (Σ reps · top load) that updates
    // as the chain rounds change. Read-only, muted, tabular for the
    // numbers so they stay legible (user has reduced vision).
    dropsetSummary: {
      marginTop: s.s2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderRadius: 6,
      backgroundColor: p.surface2,
    },
    dropsetSummaryText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.3,
      fontVariant: ['tabular-nums'],
    },
    dropsetRemoveChip: {
      width: 28,
      height: 28,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dropsetRemoveChipText: {
      color: p.ink3,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    // Slice 4.1 — inline "+ drop" affordance on each step row. Mirrors
    // the existing dropsetStepRemove width so step rows align even
    // when one half is hidden (last step shows + but no × until a
    // second step exists).
    dropsetStepAdd: {
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: r.r1,
    },
    dropsetStepAddText: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    // 36, non 40: stesso ritmo dei campi valore del FocusPanel e delle righe
    // di controllo (decisione di Antenore 2026-07-02, riconfermata il 25/07
    // quando un audit ha proposto di alzarli a 44 per il tap target). Il
    // pavimento dei 44pt NON si applica qui: hitSlop non aiuta un TextInput,
    // l'area toccabile E' il box, e alzarlo romperebbe il ritmo della riga.
    dropsetStepInput: {
      flex: 1,
      minHeight: 36,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      color: p.ink,
      paddingHorizontal: s.s2,
      fontSize: t.sizes.base,
      fontVariant: ['tabular-nums'],
    },
    dropsetStepRemove: {
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: r.r1,
    },
    dropsetStepRemovePressed: {
      opacity: 0.6,
    },
    dropsetStepRemoveText: {
      color: p.danger,
      fontSize: t.sizes.lg,
      fontWeight: '700',
    },
    dropsetFooter: {
      flexDirection: 'row',
      gap: s.s2,
    },
    dropsetFooterButton: {
      flex: 1,
      minHeight: 36,
      paddingHorizontal: s.s2,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dropsetFooterButtonDestructive: {
      borderColor: p.danger,
    },
    dropsetFooterButtonPressed: {
      opacity: 0.7,
    },
    dropsetFooterButtonText: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
    },
    dropsetFooterButtonTextDestructive: {
      color: p.danger,
    },
  });
}
